import * as Sentry from '@sentry/react';

import { consoleInfoLog } from '../../Helpers';

/**
 * Initializes Sentry for error tracking and performance monitoring.
 *
 * @param enabled {boolean}- Flag to enable or disable Sentry default true.
 * @param location {string} - Current location URL to allow in Sentry.
 * @param environment {string} - Environment string (e.g., production,local,development staging).
 * @param campaignId {string} - Campaign identifier, used as a tag in Sentry.
 * @param source {string} - Optional source of the event, used as a tag in Sentry.
 * @param refid3 {string} - The third referral ID we get from URL is used as a tag in Sentry.
 * @param refid2 {string} - The second referral ID we get from the URL is used as a tag in Sentry.
 * @param refid1 {string} - The first referral ID we get from the URL is used as a tag in Sentry.
 * @param dsn {string} - The DSN tells the Sentry SDK where to send the events to.
 */

const initReactSentry = ({
    environment,
    campaignId,
    location,
    source,
    refid3,
    refid2,
    refid1,
    dsn = 'https://5b86359019bf4330803e3e33b36d936e@sentry.io/1415478',
    enabled = true,
}) => {
    if (enabled) {
        Sentry.init({
            dsn,
            environment,
            normalizeDepth: 6,
            maxBreadcrumbs: 50,
            autoSessionTracking: false,
            integrations: [
                Sentry.contextLinesIntegration(), //Adds context lines of code around the location where the error occurred. Defaults to 7.
                Sentry.extraErrorDataIntegration(), //Logs HTTP requests and responses to track which requests may have caused an error.
            ],
            allowUrls: [location],
            denyUrls: [
                /securepubads\.g\.doubleclick\.net\/tag\/js\/gpt\.js/i,
                /^https:\/\/cadmus\.script\.ac\/[a-zA-Z0-9]+\/[a-zA-Z0-9]+\.js$/,
                /https?:\/\/cdn\.mgaru\.dev\/static\/myGaruStandalone\.js/i,
                /https:\/\/cdn\.doubleverify\.com\/dv-measurements\d+\.js/,
                /https:\/\/securepubads\.g\.doubleclick\.net(?:\/.*)?/,
                'https://c.amazon-adsystem.com/aax2/apstag.js',
                'https://static.cloudflareinsights.com/',
                'https://imasdk.googleapis.com/',
                'https://cdn.flashtalking.com/',
                'https://cdn.doubleverify.com/',
                'https://www.googletagmanager.com/gtag/js',
                // Chrome extensions
                'chrome-extension://haldlgldplgnggkjaafhelgiaglafanh/admin.js',
                /extensions\//i,
                /^chrome:\/\//i,
                /^chrome-extension:\/\//i,
            ],
            ignoreErrors: [
                'ResizeObserver loop limit exceeded',
                'ResizeObserver loop completed with undelivered notifications',
                'Non-Error promise rejection captured with value: null',
                // Unity WebGL noise
                /wasm-emscripten.*out of bounds/i,
                /WebGL context lost/i,
                // Unity WebGL GL.shaders null in Safari (87% of occurrences) - internal runtime, not fixable from frontend
                /null is not an object.*GL\.shaders/i,
                /GL\.shaders\[.*\]\.shaderType/i,
                // GA/3rd party blockers
                /Blocked a frame with origin/i,
                /Script error\.?/i,
            ],
            initialScope: {
                tags: {
                    ...(campaignId && { campaign_id: campaignId }),
                    ...(refid1 && refid1 !== '0' && { refid1 }),
                    ...(refid2 && refid2 !== '0' && { refid2 }),
                    ...(refid3 && refid3 !== '0' && { refid3 }),
                    ...(source && { source }),
                },
            },
            beforeSend(event, hint) {
                if (event.user) delete event.user;

                if (event.request) {
                    delete event.request.cookies;
                    delete event.request.headers;
                    delete event.request.env;
                }

                const originalException = hint.originalException;
                event.extra = event.extra || {};

                // Ignore aborted requests
                if (
                    originalException &&
                    originalException.name === 'AbortError'
                ) {
                    return null;
                }

                // Ignore Non-Error promise rejection captured with value: null if localhost
                const url = event.request?.url || '';
                const message =
                    event.exception?.values?.[0]?.value || event.message || '';

                if (
                    url.includes('localhost') &&
                    message.includes(
                        'Non-Error promise rejection captured with value: null'
                    )
                ) {
                    return null;
                }

                if (originalException != null) {
                    // Assign enumerable error properties to extras
                    try {
                        const keys = Object.keys(originalException);
                        if (keys.length) {
                            event.extra.errorProperties = {};
                            keys.forEach((key) => {
                                event.extra.errorProperties[key] =
                                    originalException[key];
                            });
                        }
                    } catch (error) {
                        console.warn(
                            '[sentry] Failed to assign enumerable error properties to extras',
                            error
                        );
                    }
                }

                let exceptions = event.exception;
                if (exceptions) {
                    let values =
                        exceptions.values[exceptions.values.length - 1];
                    //check if exceptions has values
                    if (values) {
                        let stacktrace = values.stacktrace;
                        //check if exception has values
                        if (stacktrace) {
                            let frames =
                                stacktrace.frames[stacktrace.frames.length - 1];
                            //check if stacktrace has frames
                            if (frames) {
                                let fileName = frames.filename;
                                //check if stacktrace has file name
                                if (fileName && event.tags) {
                                    // error_source tag to sentry event
                                    event.tags.error_source = fileName;
                                }
                            }
                        }
                    }
                }

                return event;
            },
        });
        consoleInfoLog('## Init: Sentry', 'init');
    }
};

export default initReactSentry;
